import { BaseEditor, Editor, Element, Range, Transforms } from "slate";
import { ReactEditor } from "slate-react";
import { CustomElement } from "../slate";

export interface LinksEditor extends BaseEditor {
  isLink: () => boolean;
  isValidUrl: (url: string) => void;
  toggleLink: (href?: string) => void;
}

const isLink = (editor: Editor): boolean => {
  const [match] = Editor.nodes(editor, {
    match: (n: CustomElement | any) => n.type === "link" && !!n.href,
  });
  return !!match;
};

const isValidUrl = (url: string): boolean => {
  try {
    const urlObject = new URL(url);
    return ["http:", "https:"].includes(urlObject.protocol);
  } catch (e) {
    return false;
  }
};

/**
 * TODO: Remove comment block when done
 * Cases:
 * (1) select text and click on link
 *    (a) if it has a link with in the selection, remove the link
 *    (b) if selection has no links, prompt for href and insert link
 * DEPRECATED:
 * (2) select a point, and click link
 *    (a) if point is in a link element, remove link
 *    (b) if point is not in link, prompt for href and text and insert link
 *
 * NOTE: disable link button when editor is not focused or a range selection is not available
 *
 * NOTE: leave paste to insert link block (image, title, description)
 *
 */
const toggleLink = (editor: Editor, href?: string | null) => {
  ReactEditor.focus(editor);
  const { selection } = editor;
  const isLinkSelection = isLink(editor);
  if (isLinkSelection) {
    Transforms.unwrapNodes(editor, {
      match: (n) =>
        !Editor.isEditor(n) && Element.isElement(n) && n.type === "link",
    });
  } else {
    const canInsertLink =
      ReactEditor.isFocused(editor) &&
      !!selection &&
      !Range.isCollapsed(selection);
    if (canInsertLink) {
      href = href || prompt("Enter url here..", "https://");
      if (!href) {
        return;
      }
      const text = Editor.string(editor, selection!);
      Transforms.wrapNodes(
        editor,
        { type: "link", href, children: [{ text }] },
        { split: true }
      );
      Transforms.collapse(editor, { edge: "end" });
    }
  }
};

export const withLinks = (editor: Editor) => {
  const { isInline, insertText } = editor;

  editor.isInline = (element: CustomElement) => {
    return element.type === "link" ? true : isInline(element);
  };
  editor.insertText = (text: string) => {
    if (!editor.operations.length) {
      if (isValidUrl(text)) {
        // HINT: Embeds and images handling should go here
        return;
      }
      insertText(text);
    }
  };
  editor.isLink = () => isLink(editor);
  editor.isValidUrl = (url: string) => isValidUrl(url);
  editor.toggleLink = (href?: string) => toggleLink(editor, href);
  return editor;
};
